import type { DataRoomSummary, RoomTopic } from "./types";

/**
 * What the Documents page says about a set of rooms.
 *
 * Here rather than in the component because it is an opinion — over *files*,
 * not rooms; untagged counted and shown; five rows and a remainder — and an
 * opinion is worth a test. The component only draws bars.
 */

/** How many rows the breakdown shows before it stops naming names. */
export const TOP_N = 5;

export interface Share {
  industry: string;
  files: number;
  /** 0–1 of the **tagged** files, which is what the bar is drawn from. */
  share: number;
}

export interface Breakdown {
  /** Every file in every room, tagged or not. */
  files: number;
  /** Files with a label. The shares are fractions of *this*. */
  tagged: number;
  /** Files nobody has judged. Shown beside the total, never folded in. */
  untagged: number;
  /** At most `TOP_N` rows, largest first, plus a remainder row when the
   *  tail is longer than that. */
  shares: Share[];
}

/**
 * The aggregate over a set of rooms.
 *
 * **Over files.** A room holding six transcripts contributes six: the question
 * is "what do our experts cover", and counting rooms would let one interview
 * filed on its own outweigh six filed together.
 *
 * **`untagged` is never folded into the shares.** Twelve of fourteen tagged
 * and a bar reading "70%" is a percentage of something the reader believes is
 * fourteen. So the shares are of what was actually judged, and the count that
 * was not is printed beside the total.
 */
export function breakdown(rooms: DataRoomSummary[]): Breakdown {
  const counts = new Map<string, number>();
  let files = 0;
  let tagged = 0;

  for (const room of rooms) {
    files += room.files;
    for (const entry of Object.values(room.tags ?? {})) {
      const industry = entry?.industry;
      if (!industry) continue;
      tagged += 1;
      counts.set(industry, (counts.get(industry) ?? 0) + 1);
    }
  }

  // A tag whose file has since been deleted would otherwise make `tagged`
  // exceed `files` and every share exceed what the reader can see.
  tagged = Math.min(tagged, files);

  const ranked = [...counts.entries()]
    .map(([industry, n]) => ({ industry, files: n }))
    .sort((a, b) => b.files - a.files || a.industry.localeCompare(b.industry));

  const head = ranked.slice(0, TOP_N);
  const tail = ranked.slice(TOP_N);
  const rest = tail.reduce((n, row) => n + row.files, 0);
  // Named rather than "Other": the vocabulary *has* an `Other`, and a row
  // that silently merged the two would report the model's "none of these"
  // and the page's "not in the top five" as one fact.
  if (rest > 0) head.push({ industry: `${tail.length} more industries`, files: rest });

  return {
    files,
    tagged,
    untagged: Math.max(0, files - tagged),
    shares: head.map((row) => ({
      ...row,
      share: tagged > 0 ? row.files / tagged : 0,
    })),
  };
}

/** `0.7` → `70%`. Rounded to whole points: a breakdown of fourteen files has
 *  no decimal place worth printing. */
export function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** The room's own subject, or null while nobody has asked. */
export function topicOf(room: DataRoomSummary): RoomTopic | null {
  const topic = room.topic as RoomTopic | undefined;
  return topic && (topic.topic || topic.industry) ? topic : null;
}

/** Files in this room that carry no label. The button's whole reason to be
 *  enabled, and the number beside it. */
export function untaggedIn(room: DataRoomSummary): number {
  const tagged = Object.values(room.tags ?? {}).filter((t) => t?.industry).length;
  return Math.max(0, room.files - tagged);
}

/**
 * How many bytes a pick will send.
 *
 * What the upload's progress is measured against, because bytes are what the
 * waiting is made of: twenty documents where the last one is the CIM spend most
 * of the wait on that one file, and a bar counting files would sit at 95% for
 * all of it. It is an estimate of the wait and not of the request — a multipart
 * body is these files plus a boundary and a header each — which is why the bar
 * scales each request's own reported fraction rather than adding up sizes.
 */
export function uploadSize(items: { file: { size: number } }[]): number {
  return items.reduce((sum, item) => sum + item.file.size, 0);
}
